import { HttpBackend, HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { GEOSERVER_URL_GEOJSON } from '@core/constants/constant.data'
import { FeatureCollection } from 'geojson'
import SldParser from 'geostyler-sld-parser'
import { Rule, Style, Symbolizer } from 'geostyler-style'

@Injectable({
  providedIn: 'root',
})

// In GeoServer, styling is accomplished using a markup language called Styled Layer Descriptor, or SLD
export class SLDService {
  // Uses HttpBackend so a failed style fetch falls back silently instead of showing an error toast (whichi would show if we used base httpservice)
  private readonly _http = new HttpClient(inject(HttpBackend))

  parseSLD(sldXml: string): Promise<Style | null> {
    return new Promise((resolve) => {
      const sldParser = new SldParser()

      sldParser
        .readStyle(sldXml)
        .then(({ output: geoStylerStyle }) => {
          resolve(geoStylerStyle ?? null)
        })
        .catch((error) => {
          console.error(error)
          resolve(null)
        })
    })
  }

  applyStyleToGeoJSONFeatures(geojson: FeatureCollection, style: Style): any {
    const rules = style?.rules || []

    geojson.features = geojson.features.map((feature: any) => {
      const matchedRule = this._matchFeatureToRule(feature, rules)
      const symbolizer = matchedRule?.symbolizers?.[0] || null
      const style = this._convertSymbolizerToLeafletStyle(symbolizer) || {}
      return {
        ...feature,
        style: style,
        originalSymbolizer: symbolizer, // keep original style for divIcon markers
      }
    })

    return geojson
  }

  async getStyleFromGeoserver(layerType: string) {
    if (!layerType) {
      return null
    }
    const url = `${GEOSERVER_URL_GEOJSON}?service=WMS&version=1.1.1&request=GetStyles&LAYERS=${layerType}`
    return new Promise((resolve) => {
      this._http
        .get(url, {
          responseType: 'text', // fetch raw XML as string
          headers: {
            Accept: 'application/vnd.ogc.sld+xml',
          },
        })
        .subscribe({
          next: (sldXml) => {
            return resolve(sldXml)
          },
          error: (error) => {
            console.error(error)
            return resolve(null)
          },
        })
    })
  }

  private _matchFeatureToRule(feature: any, rules: Rule[]): Rule | null {
    let bestMatchRule: Rule | null = null

    for (const rule of rules) {
      const hasIconSymbolizer = rule.symbolizers?.some((symbolizer) => symbolizer.kind === 'Icon')

      // Icon is hardcode for excess_heat pie charts
      if (
        hasIconSymbolizer &&
        (!rule.filter || this._evaluateFilter(rule.filter, feature.properties))
      ) {
        return rule
      }

      if (!this._isRuleKindSameAsGeomType(rule, feature)) {
        continue
      }

      if (!rule.filter || this._evaluateFilter(rule.filter, feature.properties)) {
        bestMatchRule = rule
      }
    }

    return bestMatchRule
  }

  private _isRuleKindSameAsGeomType(rule: Rule, feature: any): boolean {
    const kind = rule.symbolizers?.[0]?.kind || null
    const type = feature.geometry?.type || null

    if (!kind || !type) return false

    const kindMatches: Record<string, string[]> = {
      Mark: ['Point', 'MultiPoint'],
      Point: ['Point'],
      Circle: ['Point'],
      Line: ['LineString', 'MultiLineString'],
      Polygon: ['Polygon', 'MultiPolygon'],
      Fill: ['MultiPolygon'],
    }

    const expectedTypes = kindMatches[kind]

    return expectedTypes?.includes(type) ?? false
  }

  private _evaluateFilter(filter: any, props: Record<string, any>): boolean {
    if (!Array.isArray(filter)) return true

    const [op, ...args] = filter

    if (op === '&&') {
      return args.every((sub) => this._evaluateFilter(sub, props))
    }

    if (op === '||') {
      return args.some((sub) => this._evaluateFilter(sub, props))
    }

    if (op === '!') {
      return !this._evaluateFilter(args[0], props)
    }

    if (op === '<=x<=') {
      const [key, min, max] = args
      const val = props[key]
      return val >= min && val <= max
    }

    // Default: assume it's a binary comparison
    const [key, val] = args
    const actual = props[key]

    const ops: Record<string, (a: any, b: any) => boolean> = {
      '==': (a, b) => a === b,
      '!=': (a, b) => a !== b,
      '<': (a, b) => a < b,
      '<=': (a, b) => a <= b,
      '>': (a, b) => a > b,
      '>=': (a, b) => a >= b,
    }

    return ops[op]?.(actual, val) ?? false
  }

  private _convertSymbolizerToLeafletStyle(symbolizer: Symbolizer | null) {
    if (!symbolizer || typeof symbolizer !== 'object') return {}

    const kind = symbolizer.kind

    switch (kind) {
      case 'Mark':
        return {
          fillColor: symbolizer.color || '#000000',
          color: symbolizer.strokeColor || '#000000',
          weight: symbolizer.strokeWidth ?? 1,
          radius: symbolizer.radius ?? 6,
          fillOpacity: 1,
          opacity: 1,
        }

      case 'Line':
        return {
          color: symbolizer.color || '#000000',
          weight: symbolizer.width ?? 2,
          dashArray:
            Array.isArray(symbolizer.dasharray) && symbolizer.dasharray.length > 0
              ? symbolizer.dasharray.join(', ')
              : (symbolizer.dasharray ?? undefined),
          opacity: symbolizer.opacity ?? 1,
        }

      case 'Fill':
        return {
          fillColor: symbolizer.color || '#000000',
          color: symbolizer.outlineColor || '#000000',
          fillOpacity: symbolizer.fillOpacity ?? 0.5,
          weight: symbolizer.outlineWidth ?? 1,
        }

      default:
        console.warn('Unsupported symbolizer kind:', kind)
        return {}
    }
  }
}
